import { z } from "zod";
import {
  approvalStatusSchema,
  guideSourceSchema,
  ruleCategorySchema,
  ruleOriginSchema,
  severitySchema,
  wordingTypeSchema,
} from "../enums.js";
import { isoDate, optionalIsoDate } from "./common.js";

export const ruleReferenceLabelSchema = z.enum(["POSITIVE", "NEGATIVE", "BORDERLINE"]);
export type RuleReferenceLabel = z.infer<typeof ruleReferenceLabelSchema>;

export const ruleReferenceImageSchema = z.object({
  id: z.string(),
  label: ruleReferenceLabelSchema,
  note: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  createdAt: isoDate,
});
export type RuleReferenceImage = z.infer<typeof ruleReferenceImageSchema>;

export const uploadRuleReferenceSchema = z.object({
  label: ruleReferenceLabelSchema,
  note: z.string().trim().max(1000).optional(),
});
export type UploadRuleReferenceInput = z.infer<typeof uploadRuleReferenceSchema>;

/** One ○ cell of the guide matrix: the channel plus any conditional note. */
export const ruleChannelLinkSchema = z.object({
  channelId: z.string(),
  code: z.string(),
  name: z.string(),
  note: z.string().nullable(),
});
export type RuleChannelLink = z.infer<typeof ruleChannelLinkSchema>;

export const ruleCategoryLinkSchema = z.object({
  categoryId: z.string(),
  code: z.string(),
  name: z.string(),
  parentName: z.string().nullable(),
});
export type RuleCategoryLink = z.infer<typeof ruleCategoryLinkSchema>;

export const ruleVersionSchema = z.object({
  id: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  description: z.string(),
  category: ruleCategorySchema,
  severity: severitySchema,
  approvalStatus: approvalStatusSchema,
  effectiveDate: optionalIsoDate,
  requiredAction: z.string().nullable(),
  policySourceRef: z.string().nullable(),
  // Category-specific machine-checkable config (keywords, required fields, etc.)
  checkConfig: z.record(z.unknown()).nullable(),
  guideSource: guideSourceSchema.nullable(),
  wordingType: wordingTypeSchema.nullable(),
  requiredText: z.string().nullable(),
  // True for a "common" guide item that applies regardless of channel/category.
  appliesToAllChannels: z.boolean(),
  appliesToAllCategories: z.boolean(),
  origin: ruleOriginSchema,
  channels: z.array(ruleChannelLinkSchema),
  categories: z.array(ruleCategoryLinkSchema),
  referenceImages: z.array(ruleReferenceImageSchema),
  createdAt: isoDate,
  approvedAt: optionalIsoDate,
});
export type RuleVersion = z.infer<typeof ruleVersionSchema>;

export const ruleSchema = z.object({
  id: z.string(),
  activeVersionId: z.string().nullable(),
  versions: z.array(ruleVersionSchema),
  createdAt: isoDate,
});
export type Rule = z.infer<typeof ruleSchema>;

/**
 * Everything a list row needs — title/category/severity/status — without the
 * channel/category targeting, reference images, or checkConfig a list view
 * never renders. Detail pages fetch the full `ruleVersionSchema` instead.
 */
export const ruleVersionSummarySchema = z.object({
  id: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  category: ruleCategorySchema,
  severity: severitySchema,
  approvalStatus: approvalStatusSchema,
  origin: ruleOriginSchema,
  createdAt: isoDate,
  approvedAt: optionalIsoDate,
});
export type RuleVersionSummary = z.infer<typeof ruleVersionSummarySchema>;

export const ruleListItemSchema = z.object({
  id: z.string(),
  activeVersionId: z.string().nullable(),
  activeVersion: ruleVersionSummarySchema.nullable(),
  createdAt: isoDate,
});
export type RuleListItem = z.infer<typeof ruleListItemSchema>;

/**
 * List ordering. The list is paginated on the server, so sorting has to happen
 * there too — reordering one page client-side would leave the rest unsorted.
 */
export const ruleSortSchema = z.enum(["NEWEST", "OLDEST", "TITLE", "SEVERITY", "CATEGORY"]);
export type RuleSort = z.infer<typeof ruleSortSchema>;

export const ruleListResponseSchema = z.object({
  items: z.array(ruleListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type RuleListResponse = z.infer<typeof ruleListResponseSchema>;

// Channel targeting moved to the RuleVersionChannel relation, so it is no
// longer expressible here.
export const checkConfigSchema = z
  .object({
    // For PROHIBITED_EXPRESSION: phrases that may not appear.
    prohibitedPhrases: z.array(z.string().min(1)).optional(),
    // For REQUIRED_DISCLAIMER: at least one of these must appear.
    requiredPhrases: z.array(z.string().min(1)).optional(),
    // For REQUIRED_FIELD: labels/keywords that must be present.
    requiredFields: z.array(z.string().min(1)).optional(),
    // For NUMERIC_CLAIM / COMPARISON_CLAIM: trigger phrases needing substantiation.
    triggerPhrases: z.array(z.string().min(1)).optional(),
    productNames: z.array(z.string().min(1)).optional(),
    jurisdictions: z.array(z.string().min(1)).optional(),
  })
  .strict();
export type CheckConfig = z.infer<typeof checkConfigSchema>;

export const ruleChannelInputSchema = z.object({
  channelId: z.string().min(1),
  note: z.string().trim().max(300).optional(),
});
export type RuleChannelInput = z.infer<typeof ruleChannelInputSchema>;

export const createRuleSchema = z
  .object({
    title: z.string().min(1).max(300),
    description: z.string().min(1).max(4000),
    category: ruleCategorySchema,
    severity: severitySchema,
    effectiveDate: isoDate.optional(),
    requiredAction: z.string().max(2000).optional(),
    policySourceRef: z.string().max(300).optional(),
    checkConfig: checkConfigSchema.optional(),
    guideSource: guideSourceSchema.optional(),
    wordingType: wordingTypeSchema.optional(),
    requiredText: z.string().max(8000).optional(),
    appliesToAllChannels: z.boolean().default(true),
    appliesToAllCategories: z.boolean().default(true),
    channels: z.array(ruleChannelInputSchema).max(100).optional(),
    categoryIds: z.array(z.string().min(1)).max(100).optional(),
  })
  .superRefine((value, ctx) => {
    // A rule that targets nothing would silently never apply, which looks
    // identical to a working rule in the admin list.
    if (!value.appliesToAllChannels && (value.channels ?? []).length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["channels"],
        message: "Select at least one channel, or mark the rule as common to all channels.",
      });
    }
    if (!value.appliesToAllCategories && (value.categoryIds ?? []).length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["categoryIds"],
        message: "Select at least one category, or mark the rule as common to all categories.",
      });
    }
    if (value.wordingType === "FIXED" && !value.requiredText?.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["requiredText"],
        message: "Fixed wording requires the exact text that must appear.",
      });
    }
  });
export type CreateRuleInput = z.input<typeof createRuleSchema>;
export type ParsedCreateRuleInput = z.infer<typeof createRuleSchema>;

// A new version reuses the same shape; creating a version supersedes prior ones.
export const createRuleVersionSchema = createRuleSchema;
export type CreateRuleVersionInput = CreateRuleInput;

// --- Educate Sage: case-derived lessons -----------------------------------

/**
 * Multipart intake for one lesson case: the original material the marketing
 * team submitted, the compliance team's feedback, and the corrected/approved
 * material. Each material may be a file (attached separately as multipart
 * fields) or typed text; feedback may be typed or extracted server-side from
 * an uploaded `.docx`/PDF.
 */
export const draftLessonSchema = z.object({
  originalText: z.string().max(20000).optional(),
  modifiedText: z.string().max(20000).optional(),
  feedbackComments: z.string().max(8000).optional(),
});
export type DraftLessonInput = z.infer<typeof draftLessonSchema>;

/** What the AI proposes from a case; the admin reviews/edits before saving. */
export const lessonDraftSchema = z.object({
  title: z.string().min(1).max(300),
  category: ruleCategorySchema,
  severity: severitySchema,
  description: z.string().min(1).max(4000),
  requiredAction: z.string().max(2000).nullable(),
  policySourceRef: z.string().max(300).nullable(),
  // Plain-language "what this teaches" blurb shown on the lesson card.
  summary: z.string().min(1).max(600),
});
export type LessonDraft = z.infer<typeof lessonDraftSchema>;

/**
 * References to the case materials uploaded/extracted while drafting, kept
 * across the draft -> review -> save round trip so the admin's edits don't
 * require re-uploading files.
 */
export const lessonCaseFilesSchema = z.object({
  originalVaultsageFileId: z.string().nullable(),
  originalFileName: z.string().nullable(),
  originalBodyText: z.string().nullable(),
  feedbackVaultsageFileId: z.string().nullable(),
  feedbackFileName: z.string().nullable(),
  modifiedVaultsageFileId: z.string().nullable(),
  modifiedFileName: z.string().nullable(),
  modifiedBodyText: z.string().nullable(),
});
export type LessonCaseFiles = z.infer<typeof lessonCaseFilesSchema>;

export const draftLessonResponseSchema = z.object({
  draft: lessonDraftSchema,
  caseFiles: lessonCaseFilesSchema,
  feedbackComments: z.string(),
});
export type DraftLessonResponse = z.infer<typeof draftLessonResponseSchema>;

export const createLessonSchema = z.object({
  draft: lessonDraftSchema,
  feedbackComments: z.string().min(1).max(8000),
  caseFiles: lessonCaseFilesSchema,
});
export type CreateLessonInput = z.infer<typeof createLessonSchema>;

/** The source case behind a TAUGHT rule version; admin-only. */
export const lessonCaseSchema = z.object({
  id: z.string(),
  originalFileName: z.string().nullable(),
  originalBodyText: z.string().nullable(),
  feedbackComments: z.string(),
  feedbackFileName: z.string().nullable(),
  modifiedFileName: z.string().nullable(),
  modifiedBodyText: z.string().nullable(),
  generatedSummary: z.string(),
  createdAt: isoDate,
  createdByEmail: z.string(),
});
export type LessonCase = z.infer<typeof lessonCaseSchema>;

export const testLessonResponseSchema = z.object({
  reviewId: z.string(),
});
export type TestLessonResponse = z.infer<typeof testLessonResponseSchema>;

/** Multipart companion field for `POST /rules/:id/test` — which other active rules to combine with the lesson under test. */
export const testLessonRequestSchema = z.object({
  additionalRuleIds: z.array(z.string().min(1)).max(20).optional(),
});
export type TestLessonRequestInput = z.infer<typeof testLessonRequestSchema>;

/** One entry in the "try it" rule picker — any active rule, lesson or manual. */
export const testableRuleSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: ruleCategorySchema,
  severity: severitySchema,
  origin: ruleOriginSchema,
});
export type TestableRule = z.infer<typeof testableRuleSchema>;

export const updateLessonSummarySchema = z.object({
  generatedSummary: z.string().min(1).max(600),
});
export type UpdateLessonSummaryInput = z.infer<typeof updateLessonSummarySchema>;
